import React, { useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AddSessionsFields } from './AddSessionsFields';
import { SessionsTable } from './SessionsTable';
import { useCreateCalendarValues } from './useCreateCalendarValues';
import { calcSessions } from './utils/calcSessions';
import { CLASS_SESSIONS } from '../../config/apiPaths';
import { ClassRecord, ClassStudentGroup } from '../../context/types';
import { getUrls } from '../../utils/url';

const Container = styled.div``;

const Title = styled.h1`
    font-size: 20px;
    font-weight: 400;
    margin: 0;
`;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    margin-bottom: 1rem;
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 1rem;

    button {
        margin-left: 0.5rem;
    }
`;

const StyledSpinner = styled(CircularProgress)`
    color: ${(props) => props.theme.colors.white};
    margin: 3px 8px;
`;

const StyledAlert = styled(Alert)`
    margin-bottom: 1rem;
`;

const SessionsContainer = styled('div')`
    margin: 0 0 1rem 0;
`;

export interface EditFormProps {
    onFinish: () => void;
    onCancel: () => void;
    classInfo: ClassRecord;
    classUuid: string;
    classGroups?: ClassStudentGroup[] | undefined;
    classSessionGroupUuid?: string;
    label?: string;
}

const SessionAddForm = (props: EditFormProps) => {
    const { onCancel, onFinish, classInfo, classUuid, classSessionGroupUuid, label, classGroups } = props;
    const { t } = useTranslation('calendar');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { schema, defaultValues } = useCreateCalendarValues();

    const { control, handleSubmit, watch } = useForm({ defaultValues, resolver: yupResolver(schema) });
    const watchAllFields = watch();

    const { sessions, isOvernight } = useMemo(() => calcSessions(watchAllFields), [watchAllFields]);

    const onSubmit = async () => {
        setLoading(true);
        setError('');

        const payload = {
            sessions: sessions.map(({ start_time, end_time }) => ({ start_time, end_time })),
            class_id: classUuid,
            timezone: watchAllFields.timezone
        };

        const { api } = getUrls();

        try {
            await axios.post(`${api}${CLASS_SESSIONS}`, payload);
            onFinish();
        } catch (err: any) {
            setLoading(false);
            setError(err.message);
        }
    };

    return (
        <Container>
            <Header>
                <Title>{t('add-dates', { className: classInfo.class_name })}</Title>
            </Header>
            {error && <StyledAlert severity="error">{error}</StyledAlert>}
            <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <SessionsContainer>
                    <AddSessionsFields
                        control={control}
                        isOvernight={isOvernight}
                        classSessionGroupUuid={classSessionGroupUuid}
                        label={label}
                        classGroups={classGroups}
                    />
                </SessionsContainer>
                <SessionsTable sessions={sessions} />

                <Footer>
                    <Button variant={'outlined'} onClick={onCancel}>
                        {t('cancel')}
                    </Button>
                    <Button variant={'contained'} type={'submit'}>
                        {loading ? <StyledSpinner size={18} /> : t('save')}
                    </Button>
                </Footer>
            </StyledForm>
        </Container>
    );
};

export default SessionAddForm;
